import React from 'react';
import { Mail, Clock, Users, Send, Pause, Edit, Trash2, Play } from 'lucide-react';
import type { Subscription } from '@/types/reporting-v2';

interface SubscriptionCardProps {
  subscription: Subscription;
  onSendNow?: (subscriptionId: string) => void;
  onPause?: (subscriptionId: string) => void;
  onResume?: (subscriptionId: string) => void;
  onEdit?: (subscriptionId: string) => void;
  onDelete?: (subscriptionId: string) => void;
}

export default function SubscriptionCard({
  subscription,
  onSendNow,
  onPause,
  onResume,
  onEdit,
  onDelete,
}: SubscriptionCardProps) {
  const formatDate = (date: string) => {
    return new Date(date).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const successRate = subscription.delivery_stats.total_deliveries > 0
    ? Math.round((subscription.delivery_stats.successful_deliveries / subscription.delivery_stats.total_deliveries) * 100)
    : 0;

  return (
    <div className="card card-hover">
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900">{subscription.subscription_name}</h3>
          {subscription.description && (
            <p className="text-sm text-gray-500 mt-1">{subscription.description}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span
            className={`px-2 py-1 rounded-full text-xs font-medium ${
              subscription.is_active
                ? 'bg-green-100 text-green-700'
                : 'bg-gray-100 text-gray-700'
            }`}
          >
            {subscription.is_active ? 'Active' : 'Inactive'}
          </span>
        </div>
      </div>

      <div className="space-y-2 mb-4">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Mail className="w-4 h-4" />
          <span>Next: {formatDate(subscription.next_delivery)}</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Clock className="w-4 h-4" />
          <span className="capitalize">{subscription.frequency}</span>
          <span className="mx-1">•</span>
          <span className="capitalize">{subscription.report_type.replace('_', ' ')}</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Users className="w-4 h-4" />
          <span>{subscription.recipients.length} recipient{subscription.recipients.length !== 1 ? 's' : ''}</span>
        </div>
      </div>

      <div className="flex items-center gap-4 mb-4 text-xs text-gray-500">
        <div>
          <span className="font-medium text-gray-900">{subscription.delivery_stats.total_deliveries}</span> sent
        </div>
        <div>
          <span className="font-medium text-green-600">{subscription.delivery_stats.successful_deliveries}</span> success
        </div>
        {subscription.delivery_stats.partial_deliveries > 0 && (
          <div>
            <span className="font-medium text-yellow-600">{subscription.delivery_stats.partial_deliveries}</span> partial
          </div>
        )}
        <div>
          <span className="font-medium text-red-600">{subscription.delivery_stats.failed_deliveries}</span> failed
        </div>
        <div>
          <span className="font-medium text-gray-900">{successRate}%</span> rate
        </div>
      </div>

      <div className="flex items-center gap-2 pt-3 border-t">
        <button
          onClick={() => onSendNow?.(subscription.subscription_id)}
          className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded transition-colors"
          title="Send Now"
        >
          <Send className="w-4 h-4" />
          Send
        </button>
        {subscription.is_active ? (
          <button
            onClick={() => onPause?.(subscription.subscription_id)}
            className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-yellow-600 hover:bg-yellow-50 rounded transition-colors"
            title="Pause"
          >
            <Pause className="w-4 h-4" />
            Pause
          </button>
        ) : (
          <button
            onClick={() => onResume?.(subscription.subscription_id)}
            className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-green-600 hover:bg-green-50 rounded transition-colors"
            title="Resume"
          >
            <Play className="w-4 h-4" />
            Resume
          </button>
        )}
        <button
          onClick={() => onEdit?.(subscription.subscription_id)}
          className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-50 rounded transition-colors"
          title="Edit"
        >
          <Edit className="w-4 h-4" />
          Edit
        </button>
        <button
          onClick={() => onDelete?.(subscription.subscription_id)}
          className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50 rounded transition-colors ml-auto"
          title="Delete"
        >
          <Trash2 className="w-4 h-4" />
          Delete
        </button>
      </div>
    </div>
  );
}
